<template>
  <div class="operations-page">
    <header class="page-heading">
      <div>
        <div class="eyebrow">INTERNAL OPERATIONS</div>
        <h1>{{ isCurator ? 'Curator accounts' : 'Collection pipeline' }}</h1>
        <p>{{ isCurator ? 'Account setup, content planning, and progress across your curators.' : 'Production, publication, and marketing across every collection.' }}</p>
      </div>
      <div class="heading-actions">
        <el-button aria-label="Refresh" icon="el-icon-refresh" :loading="loading" :disabled="isSaving" @click="loadRows">Refresh</el-button>
        <el-button :aria-label="isCurator ? 'Add curator' : 'Add collection idea'" type="primary" icon="el-icon-plus" :disabled="isSaving" @click="openPlan(null)">{{ isCurator ? 'Add curator' : 'Add collection idea' }}</el-button>
      </div>
    </header>

    <section class="table-panel" :aria-label="isCurator ? 'Curator management' : 'Collection management'">
      <div class="view-bar">
        <div class="view-tabs" role="group" aria-label="Table view">
          <button v-for="view in views" :key="view" :class="{ active: activeView === view }" @click="changeView(view)">{{ view }}</button>
        </div>
        <el-popover placement="bottom-end" width="280" trigger="click">
          <div class="column-picker">
            <strong>Visible columns</strong>
            <div class="picker-list">
              <div v-for="column in orderedColumns" :key="column.key" class="picker-item" draggable="true" @dragstart="pickStart(column.key, $event)" @dragover.prevent @drop="pickDrop(column.key, $event)">
                <el-checkbox :value="visibleKeys.includes(column.key)" :aria-label="`Show ${column.label}`" @change="toggleColumn(column.key)"><i class="el-icon-rank picker-handle" /> {{ column.label }}</el-checkbox>
              </div>
            </div>
            <div class="picker-hint">Drag to reorder columns</div>
            <el-button size="mini" type="text" class="reset-view" icon="el-icon-refresh-left" @click="resetView">Reset this view</el-button>
          </div>
          <el-button slot="reference" size="small" icon="el-icon-s-operation">Columns</el-button>
        </el-popover>
      </div>
      <div class="toolbar">
        <el-select v-model="recordKind" size="small" aria-label="Record type" :disabled="isSaving" @change="resetPage">
          <el-option label="Live & planned" value="" /><el-option label="Live records only" value="existing" /><el-option label="Planned records only" value="planned" />
        </el-select>
        <el-input v-model="search" clearable size="small" prefix-icon="el-icon-search" :placeholder="isCurator ? 'Search name, email, or ID' : 'Search collection title or ID'" label="Search records" maxlength="200" :disabled="isSaving" @input="scheduleSearch" />
        <el-select v-if="isCurator" v-model="scope" size="small" aria-label="Account scope" :disabled="isSaving" @change="resetPage">
          <el-option label="Curators & tracked accounts" value="curators" />
          <el-option label="All accounts" value="all" />
        </el-select>
        <el-select v-else v-model="curatorId" clearable filterable remote :remote-method="searchCurators" :loading="curatorLoading" size="small" placeholder="All curator accounts" aria-label="Filter by curator" :disabled="isSaving" @change="changeCurator">
          <el-option v-for="curator in curatorOptions" :key="curator.id" :value="curator.id" :label="curator.name || curator.user_email || curator.id" />
        </el-select>
        <el-select v-model="filterKey" clearable filterable size="small" placeholder="Filter a field" aria-label="Filter field" :disabled="isSaving" @change="changeFilter">
          <el-option v-for="column in allColumns" :key="column.key" :value="column.key" :label="column.label" />
        </el-select>
        <template v-if="filterKey">
          <el-select v-if="filterColumn.type === 'boolean'" v-model="filterValue" size="small" aria-label="Checkbox filter" :disabled="isSaving" @change="resetPage">
            <el-option label="Checked / Yes" value="true" /><el-option label="Unchecked / No" value="false" />
          </el-select>
          <el-select v-else-if="filterColumn.type === 'select' || filterColumn.type === 'multi' || filterColumn.key === 'status'" v-model="filterValue" filterable allow-create default-first-option clearable size="small" placeholder="Choose or type a value" aria-label="Filter value" :disabled="isSaving" @change="resetPage">
            <el-option v-for="option in filterColumn.options" :key="option" :label="option" :value="option" />
          </el-select>
          <el-input v-else v-model="filterValue" clearable size="small" :placeholder="filterColumn.type === 'number' ? 'Exact count' : 'Contains…'" label="Filter value" :disabled="isSaving" @input="scheduleSearch" />
        </template>
        <span class="view-count"><strong>{{ total === null ? '—' : total.toLocaleString() }}</strong> {{ isCurator ? 'accounts' : 'collections' }} in this view</span>
      </div>
      <p v-if="isCurator" class="scope-help">Includes collection owners, tracked accounts, and planned curators. Choose “All accounts” to track another existing account. Turn on Featured and select the category pages where this curator should appear in Explore Plaza; an empty selection keeps them on the main Explore page only. <span class="internal-note"><i class="el-icon-lock" /> Internal fields are visible only to admins. <span class="auto-label">AUTO</span> fields update from record data.</span></p>
      <p v-else class="scope-help">Includes collection ideas and all publication states. Marketing statuses record progress; changing them does not send or publish content. <span class="internal-note"><i class="el-icon-lock" /> Internal fields are visible only to admins. <span class="auto-label">AUTO</span> fields update from record data.</span></p>
      <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" class="load-error" />
      <div class="save-status" aria-live="polite"><i :class="isSaving ? 'el-icon-loading' : 'el-icon-circle-check'" /> {{ isSaving ? 'Saving changes…' : saveMessage }}</div>
      <el-table
        ref="table" v-loading="loading" :data="rows" row-key="id" border
        :empty-text="error ? 'Records could not be loaded. Use Refresh to try again.' : 'No matching records. Try another search or filter.'"
        @sort-change="changeSort" @header-dragend="saveColumnWidth"
      >
        <el-table-column prop="name" :label="isCurator ? 'Curator account' : 'Collection / Content idea'" fixed="left" :width="widths.name || 265" sortable="custom">
          <template slot-scope="{ row }">
            <div class="record-name">{{ row.name || 'Untitled' }} <el-tag v-if="row.record_kind === 'planned'" size="mini" type="warning">Planned</el-tag></div>
            <span class="record-id">{{ row.id }}</span>
            <div v-if="isCurator" class="record-links"><router-link :to="{ path: '/lf-admin/collections', query: { user_id: row.id, curator_name: row.name || row.user_email } }">View collections <i class="el-icon-right" /></router-link></div>
            <div v-else-if="row.record_kind === 'existing'" class="record-links collection-links">
              <a :href="publicCollectionUrl(row)" target="_blank" rel="noopener noreferrer" :aria-label="`View public page for ${row.name || 'Untitled'} (opens in a new tab)`">View public page <i class="el-icon-top-right" aria-hidden="true" /></a>
              <el-button type="text" size="mini" :disabled="isSaving" @click="openCollectionEditor(row)">Edit collection fields</el-button>
              <el-button type="text" size="mini" :disabled="isSaving" @click="openTransfer(row)">Change curator</el-button>
            </div>
            <div v-if="row.record_kind === 'planned'" class="plan-actions">
              <el-button type="text" size="mini" :disabled="isSaving" @click="openPlan(row)">Edit plan</el-button>
              <el-button type="text" size="mini" :disabled="isSaving" @click="openPlan(row, 'link')">Link live record</el-button>
            </div>
            <div v-else-if="isCurator" class="plan-actions">
              <el-button type="text" size="mini" :disabled="openingCreator === row.id" @click="openCreatorPage(row)">Open creator page</el-button>
            </div>
            <div v-else class="plan-actions">
              <el-button type="text" size="mini" class="delete-action" :disabled="isSaving" @click="openDelete(row)">Delete collection</el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column v-for="column in columns" :key="column.key" :prop="column.key" :width="widths[column.key] || column.width || 210" sortable="custom">
          <template slot="header" slot-scope="header"><span :data-field="header.column.property">{{ column.label }}</span> <el-tooltip v-if="column.hint" :content="column.hint" placement="top"><i class="el-icon-question header-hint" /></el-tooltip> <span v-if="!column.manual && !column.switch && !column.liveSetting" class="auto-label">AUTO</span></template>
          <template slot-scope="{ row }">
            <field-editor v-if="column.manual" :key="row.id + column.field + (conflicts[row.id] ? ':conflict' : '')" :field="column" :value="row.fields[column.field]" :disabled="loading || !!saving[row.id] || !!conflicts[row.id]" @save="value => saveField(row, column, value)" />
            <el-select v-else-if="column.key === 'featured_catalogue_section_codes'" :value="row.featured_catalogue_section_codes || []" multiple filterable clearable size="small" class="category-page-select" placeholder="Select category pages" :aria-label="column.label" :disabled="row.record_kind !== 'existing' || loading || !!saving[row.id] || !!conflicts[row.id]" @change="value => saveLiveSetting(row, column, value)">
              <el-option v-for="section in catalogueSectionOptions" :key="section.value" :value="section.value" :label="section.label" />
            </el-select>
            <div v-else-if="column.key === 'featured'" class="featured-cell">
              <el-switch :value="!!row.featured" :disabled="row.record_kind !== 'existing' || loading || !!saving[row.id] || !!conflicts[row.id]" aria-label="Featured" @change="value => saveLiveSetting(row, column, value)" />
              <el-button type="text" size="mini" :disabled="row.record_kind !== 'existing' || loading || !!saving[row.id] || !!conflicts[row.id]" :aria-label="`Choose featured category pages for ${row.name || row.user_email}`" @click="openFeaturedCategories(row)">Categories<span v-if="row.featured_catalogue_section_codes && row.featured_catalogue_section_codes.length"> ({{ row.featured_catalogue_section_codes.length }})</span></el-button>
            </div>
            <el-switch v-else-if="column.switch" :value="!!row[column.key]" :disabled="row.record_kind !== 'existing' || loading || !!saving[row.id] || !!conflicts[row.id]" :aria-label="column.label" @change="value => saveLiveSetting(row, column, value)" />
            <span v-else-if="column.type === 'boolean'" class="boolean-value" :class="{ checked: row[column.key] }"><i :class="row[column.key] ? 'el-icon-circle-check' : 'el-icon-minus'" /> {{ row[column.key] ? 'Yes' : 'No' }}</span>
            <el-tag v-else-if="column.key === 'status'" size="small" :type="row.status === 'PUBLISHED' ? 'success' : 'info'">{{ row.status || 'PRIVATE' }}</el-tag>
            <span v-else>{{ row[column.key] === 0 ? 0 : (row[column.key] || '—') }}</span>
          </template>
        </el-table-column>
      </el-table>
      <footer class="table-footer">
        <span>Changes save per field · Select fields accept custom options</span>
        <el-pagination :current-page="page" :page-size="pageSize" :page-sizes="[25, 50, 100]" :total="total || 0" :disabled="loading || isSaving" layout="total, sizes, prev, pager, next" @current-change="goToPage" @size-change="changePageSize" />
      </footer>
    </section>
    <plan-dialog v-if="planDialog" :visible.sync="planDialog" :entity="entity" :record="selectedPlan" :mode="planMode" :default-owner="curatorId" @saved="planSaved" />
    <el-dialog title="Delete collection" :visible.sync="deleteDialog" width="480px" :close-on-click-modal="false">
      <p class="dialog-help">This permanently removes “{{ deleteTarget ? (deleteTarget.name || 'Untitled') : '' }}” from LearningFirst. An archived copy stays in the database for recovery.</p>
      <el-checkbox v-model="deletePosts">Also delete the posts inside this collection</el-checkbox>
      <p class="dialog-help">If unchecked, the posts are kept but detached from the deleted collection.</p>
      <el-alert v-if="deleteError" :title="deleteError" type="error" :closable="false" show-icon />
      <span slot="footer">
        <el-button :disabled="deleting" @click="deleteDialog = false">Cancel</el-button>
        <el-button type="danger" :loading="deleting" @click="performDelete">Delete collection</el-button>
      </span>
    </el-dialog>
    <el-dialog v-if="isCurator" title="Featured category pages" :visible.sync="featuredCategoryDialog" width="520px" :close-on-click-modal="false">
      <p v-if="featuredCategoryRow">Choose the Explore category pages for <strong>{{ featuredCategoryRow.name || featuredCategoryRow.user_email }}</strong>. The curator appears there when Featured is on.</p>
      <el-select v-model="featuredCategoryDraft" multiple filterable clearable placeholder="Select category pages" aria-label="Featured category pages" class="category-dialog-select" :disabled="!!(featuredCategoryRow && saving[featuredCategoryRow.id])">
        <el-option v-for="section in catalogueSectionOptions" :key="section.value" :value="section.value" :label="section.label" />
      </el-select>
      <span slot="footer">
        <el-button :disabled="!!(featuredCategoryRow && saving[featuredCategoryRow.id])" @click="featuredCategoryDialog = false">Cancel</el-button>
        <el-button type="primary" :loading="!!(featuredCategoryRow && saving[featuredCategoryRow.id])" @click="submitFeaturedCategories">Save categories</el-button>
      </span>
    </el-dialog>
    <el-dialog v-if="!isCurator" title="Change collection curator" :visible.sync="transferDialog" width="520px" :close-on-click-modal="false">
      <p v-if="transferRow"><strong>{{ transferRow.name }}</strong><br>Current curator: {{ transferRow.user_name || transferRow.user_email || transferRow.user_id }}</p>
      <el-form label-position="top">
        <el-form-item label="New curator account">
          <el-select v-model="transferTargetId" filterable remote :remote-method="searchTransferTargets" :loading="transferTargetLoading" placeholder="Search account name or email" style="width:100%" @change="previewTransfer">
            <el-option v-for="account in transferTargets" :key="account.id" :value="account.id" :label="`${account.name || account.user_email || account.id} (${account.user_email || account.id})`" />
          </el-select>
        </el-form-item>
      </el-form>
      <p v-if="transferPreviewLoading">Checking collection relationships…</p>
      <template v-if="transferPreview && !transferPreviewLoading">
        <p>This moves the collection and {{ transferPreview.post_count }} associated posts to <strong>{{ transferPreview.target_name }}</strong> ({{ transferPreview.target_email }}). Titles, lessons, and learner records stay in place.</p>
        <p v-if="transferPreview.bundle_count">{{ transferPreview.bundle_count }} existing bundle(s) keep this collection. <span v-if="transferPreview.peer_owner_ids.length">{{ transferPreview.peer_owner_ids.length }} bundle curator(s) will track it as a peer collection.</span></p>
        <p v-if="transferPreview.collection_order_count || transferPreview.post_order_count">{{ transferPreview.collection_order_count }} collection order(s) and {{ transferPreview.post_order_count }} post order(s) keep their recorded sellers.</p>
        <el-alert v-if="transferPreview.blocked_reasons.length" title="This collection cannot be transferred automatically" type="warning" :closable="false">
          <ul><li v-for="reason in transferPreview.blocked_reasons" :key="reason">{{ reason }}</li></ul>
        </el-alert>
        <p v-else>Review the target account before confirming.</p>
      </template>
      <el-alert v-if="transferError" :title="transferError" type="error" :closable="false" />
      <span slot="footer">
        <el-button @click="transferDialog = false">Cancel</el-button>
        <el-button type="primary" :loading="transferSubmitting" :disabled="!transferPreview || transferPreviewLoading || !!transferPreview.blocked_reasons.length" @click="submitTransfer">Confirm transfer</el-button>
      </span>
    </el-dialog>
    <collection-editor v-if="!isCurator && selectedCollection" :visible="!!selectedCollection" :row="selectedCollection" @close="selectedCollection = null" @saved="loadRows" />
  </div>
</template>

<script>
import FieldEditor from './FieldEditor.vue'
import PlanDialog from './PlanDialog.vue'
import CollectionEditor from './CollectionEditor.vue'
import { getAdminOperationsConfig, listAdminCurators, listAdminCollections, updateAdminOperations, deleteAdminCollection, createCreatorEntryTicket, previewAdminCollectionTransfer, transferAdminCollection } from '@/apis/lfAdmin'
import { CATALOGUE_SECTION_OPTIONS } from '@/constants/catalogueSections'
import { publicContentUrl } from '@/utils/publicContentLinks'

export default {
  name: 'AdminOperationsTable',
  components: { FieldEditor, PlanDialog, CollectionEditor },
  props: { entity: { type: String, required: true } },
  data() {
    return {
      planDialog: false, planMode: 'edit', selectedPlan: null, selectedCollection: null, recordKind: '', fields: [], rows: [], total: null, page: 1, pageSize: 25, loading: false, error: '',
      deleteDialog: false, deleteTarget: null, deletePosts: false, deleting: false, deleteError: '', openingCreator: '',
      search: '', scope: 'curators', filterKey: '', filterValue: '', sort: 'name', direction: 'asc',
      activeView: 'Overview', visibleKeys: [], columnOrder: [], widths: {}, storedViews: {}, dragKey: '', saving: {}, conflicts: {}, saveMessage: 'Ready to edit',
      featuredCategoryDialog: false, featuredCategoryRow: null, featuredCategoryDraft: [],
      reloadAfterSave: false, curatorId: '', curatorOptions: [], curatorLoading: false, requestId: 0, curatorRequestId: 0, searchTimer: null,
      transferDialog: false, transferRow: null, transferTargetId: '', transferTargets: [], transferTargetLoading: false,
      transferPreview: null, transferPreviewLoading: false, transferSubmitting: false, transferError: '', transferRequestId: 0
    }
  },
  computed: {
    isCurator() { return this.entity === 'curator' },
    catalogueSectionOptions() { return CATALOGUE_SECTION_OPTIONS },
    isSaving() { return Object.values(this.saving).some(Boolean) },
    views() { return this.isCurator ? ['Overview', 'Setup', 'Planning', 'All fields'] : ['Overview', 'Production', 'Marketing', 'All fields'] },
    autoColumns() {
      const column = (key, label, type = 'text', width = 200) => ({ key, label, type, width })
      return this.isCurator ? [
        column('user_email', 'Account email', 'text', 280), { ...column('featured', 'Featured', 'boolean', 230), switch: true },
        { ...column('featured_catalogue_section_codes', 'Featured category pages', 'multi', 310), liveSetting: true, options: CATALOGUE_SECTION_OPTIONS.map(option => option.value) },
        column('knowit_account_id', 'Account handle'),
        column('collection_count', 'Collections', 'number', 145), column('published_count', 'Published', 'number', 145),
        column('planned_count', 'Planned collections', 'number', 185), column('batch_count', 'Batch count', 'number', 145), column('has_signature_intro', 'Profile text present', 'boolean'),
        column('has_avatar', 'Avatar present', 'boolean', 175), column('account_status', 'Account access')
      ] : [
        column('user_name', 'Curator account'), column('user_email', 'Account email', 'text', 280),
        { ...column('status', 'Publication status'), options: ['PRIVATE', 'DRAFT', 'PUBLISHED', 'PREORDER', 'TEASER', 'REQUEST', 'HIDDEN', 'PLANNED'] },
        column('has_cover', 'Collection cover', 'boolean'), column('has_brief', 'Collection brief', 'boolean', 185),
        column('has_thesis_essay', 'Thesis essay', 'boolean', 150), column('has_big_ideas', '10 big ideas', 'boolean', 160), column('after_paywall', 'Collection after paywall', 'boolean', 200),
        column('visible_in_recommendation', 'Visible in recommendation', 'boolean', 215),
        column('learner_level', 'Learner level', 'text', 180),
        column('is_public', 'Public on LF?', 'boolean'),
        column('is_listed', 'Listed in public search', 'boolean', 225)
      ]
    },
    allColumns() { return [...this.autoColumns, ...this.fields.map(f => ({ ...f, field: f.key, key: `fields.${f.key}`, manual: true, width: f.type === 'text' || f.type === 'multi' ? 280 : 230 }))] },
    orderedColumns() {
      if (!this.columnOrder.length) return this.allColumns
      const rank = key => { const index = this.columnOrder.indexOf(key); return index < 0 ? this.columnOrder.length + this.allColumns.findIndex(c => c.key === key) : index }
      return [...this.allColumns].sort((a, b) => rank(a.key) - rank(b.key))
    },
    columns() { return this.orderedColumns.filter(c => this.visibleKeys.includes(c.key)) },
    filterColumn() { return this.allColumns.find(c => c.key === this.filterKey) || {} }
  },
  watch: {
    '$route.query.user_id'(value) {
      if (this.isCurator) return
      this.curatorId = value || ''
      this.seedCuratorOption()
      this.resetPage()
    }
  },
  async created() {
    this.curatorId = this.isCurator ? '' : (this.$route.query.user_id || '')
    this.seedCuratorOption()
    await this.loadRows()
    if (!this.isCurator) this.searchCurators('')
  },
  beforeDestroy() { clearTimeout(this.searchTimer); this.requestId++; this.curatorRequestId++; this.transferRequestId++ },
  methods: {
    publicCollectionUrl(row) {
      return publicContentUrl('collection', { collection_id: row.id }, 'id')
    },
    openFeaturedCategories(row) {
      this.featuredCategoryRow = row
      this.featuredCategoryDraft = (row.featured_catalogue_section_codes || []).slice()
      this.featuredCategoryDialog = true
    },
    async submitFeaturedCategories() {
      if (!this.featuredCategoryRow) return
      const saved = await this.saveLiveSetting(this.featuredCategoryRow, {
        key: 'featured_catalogue_section_codes', label: 'Featured category pages'
      }, this.featuredCategoryDraft)
      if (saved) this.featuredCategoryDialog = false
    },
    openTransfer(row) {
      this.transferRow = row
      this.transferTargetId = ''
      this.transferPreview = null
      this.transferError = ''
      this.transferDialog = true
      this.searchTransferTargets('')
    },
    async searchTransferTargets(q) {
      this.transferTargetLoading = true
      try {
        const data = this.result(await listAdminCurators({ q, scope: 'all', page_size: 50 }))
        this.transferTargets = data.rows.filter(account => account.record_kind === 'existing')
      } catch (error) { this.transferError = error.message }
      finally { this.transferTargetLoading = false }
    },
    async previewTransfer() {
      const requestId = ++this.transferRequestId
      this.transferPreview = null
      this.transferError = ''
      if (!this.transferTargetId || !this.transferRow) return
      this.transferPreviewLoading = true
      try {
        const data = this.result(await previewAdminCollectionTransfer({ collection_id: this.transferRow.id, target_user_id: this.transferTargetId }))
        if (requestId === this.transferRequestId) this.transferPreview = data
      } catch (error) { if (requestId === this.transferRequestId) this.transferError = error.message }
      finally { if (requestId === this.transferRequestId) this.transferPreviewLoading = false }
    },
    async submitTransfer() {
      if (!this.transferPreview || this.transferPreview.blocked_reasons.length || this.transferSubmitting) return
      this.transferSubmitting = true
      this.transferError = ''
      try {
        const data = this.result(await transferAdminCollection({
          collection_id: this.transferPreview.collection_id,
          target_user_id: this.transferPreview.target_user_id,
          expected_source_user_id: this.transferPreview.source_user_id
        }))
        this.transferDialog = false
        this.$message.success(`Collection transferred to ${this.transferPreview.target_name}`)
        if (data.sync_warnings && data.sync_warnings.length) this.$message.warning(data.sync_warnings.join('; '))
        this.loadRows()
      } catch (error) {
        this.transferError = error.message
        if (error.code === -409) this.loadRows()
      } finally { this.transferSubmitting = false }
    },
    openCollectionEditor(row) { this.selectedCollection = row },
    openPlan(row, mode = 'edit') { this.selectedPlan = row; this.planMode = mode; this.planDialog = true },
    planSaved(kind) {
      if (!this.selectedPlan || this.planMode === 'link') {
        this.search = ''; this.filterKey = ''; this.filterValue = ''
        this.recordKind = kind === 'tracked' || this.planMode === 'link' ? 'existing' : 'planned'
      }
      this.resetPage()
      if (!this.isCurator) this.searchCurators('')
    },
    openDelete(row) { this.deleteTarget = row; this.deletePosts = false; this.deleteError = ''; this.deleteDialog = true },
    async openCreatorPage(row) {
      if (this.openingCreator) return
      this.openingCreator = row.id
      try {
        const data = this.result(await createCreatorEntryTicket({ id: row.id }))
        window.open(data.url, '_blank', 'noopener')
        this.$message.success(`Opening the creator page for ${row.name || row.id}; the entry link expires in 5 minutes.`)
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        if (this.openingCreator === row.id) this.openingCreator = ''
      }
    },
    async performDelete() {
      if (!this.deleteTarget || this.deleting) return
      this.deleting = true; this.deleteError = ''
      try {
        this.result(await deleteAdminCollection({ entity: this.entity, id: this.deleteTarget.id, delete_posts: this.deletePosts }))
        this.$message.success(`Deleted “${this.deleteTarget.name || 'Untitled'}”`)
        this.deleteDialog = false
        this.loadRows()
      } catch (error) {
        this.deleteError = error.message
      } finally {
        this.deleting = false
      }
    },
    result(response) {
      const result = response && response.data
      if (!result || Number(result.code) !== 0 || !result.data) {
        const error = new Error(result && typeof result.msg === 'string' ? result.msg : 'Unable to load operations data. Check that the admin backend is available.')
        error.code = result && Number(result.code)
        throw error
      }
      return result.data
    },
    seedCuratorOption() {
      if (this.curatorId && !this.curatorOptions.some(c => c.id === this.curatorId)) this.curatorOptions.push({ id: this.curatorId, name: this.$route.query.curator_name || this.curatorId })
    },
    async loadRows() {
      if (this.isSaving) { this.reloadAfterSave = true; return }
      this.reloadAfterSave = false
      clearTimeout(this.searchTimer)
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        if (!this.fields.length) {
          const config = this.result(await getAdminOperationsConfig({ entity: this.entity }))
          if (requestId !== this.requestId) return
          this.fields = config.fields
          this.restorePreferences()
        }
        const params = { page: this.page, page_size: this.pageSize, q: this.search, scope: this.scope, record_kind: this.recordKind, sort: this.sort, direction: this.direction }
        if (this.curatorId) params.user_id = this.curatorId
        if (this.filterKey && this.filterValue !== '') { params.filter_key = this.filterKey; params.filter_value = this.filterValue }
        const data = this.result(await (this.isCurator ? listAdminCurators(params) : listAdminCollections(params)))
        if (requestId !== this.requestId) return
        this.rows = data.rows
        this.total = Number(data.total)
        this.conflicts = {}
        if (!this.rows.length && this.page > 1 && this.total <= (this.page - 1) * this.pageSize) {
          this.page = Math.max(1, Math.ceil(this.total / this.pageSize))
          return this.loadRows()
        }
      } catch (error) {
        if (requestId !== this.requestId) return
        this.error = error.message
        this.rows = []
        this.total = null
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    scheduleSearch() { clearTimeout(this.searchTimer); this.searchTimer = setTimeout(() => this.resetPage(), 350) },
    resetPage() { this.page = 1; this.loadRows() },
    goToPage(page) { this.page = page; this.loadRows() },
    changePageSize(size) { this.pageSize = size; this.resetPage() },
    changeSort({ prop, order }) { this.sort = order ? prop : 'name'; this.direction = order === 'descending' ? 'desc' : 'asc'; this.resetPage() },
    changeFilter() { this.filterValue = ''; this.resetPage() },
    changeCurator() {
      const query = { ...this.$route.query }
      delete query.curator_name
      if (this.curatorId) query.user_id = this.curatorId
      else delete query.user_id
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    async searchCurators(q) {
      const requestId = ++this.curatorRequestId
      this.curatorLoading = true
      try {
        const data = this.result(await listAdminCurators({ q, page_size: 50 }))
        if (requestId !== this.curatorRequestId) return
        const selected = this.curatorOptions.find(c => c.id === this.curatorId)
        this.curatorOptions = data.rows
        if (selected && !this.curatorOptions.some(c => c.id === selected.id)) this.curatorOptions.unshift(selected)
      } catch (error) { if (requestId === this.curatorRequestId) this.$message.error('Could not load curator choices. Try searching again.') }
      finally { if (requestId === this.curatorRequestId) this.curatorLoading = false }
    },
    async saveField(row, column, value) {
      if (this.saving[row.id] || this.conflicts[row.id] || JSON.stringify(row.fields[column.field]) === JSON.stringify(value)) return
      this.$set(this.saving, row.id, true)
      try {
        const data = this.result(await updateAdminOperations({ entity: this.entity, id: row.id, version: row.version, field: column.field, value }))
        this.$set(row, 'fields', data.fields)
        row.version = data.version
        row.updated_at = data.updated_at
        if (this.isCurator) row.batch_count = data.fields.batches.length
        const definition = this.fields.find(f => f.key === column.field)
        if (definition && ['select', 'multi'].includes(definition.type)) {
          for (const option of (Array.isArray(value) ? value : [value])) if (option && !definition.options.includes(option)) definition.options.push(option)
        }
        this.saveMessage = `Saved ${column.label} · ${new Date().toLocaleTimeString()}`
      } catch (error) {
        if (error.code === -409) this.$set(this.conflicts, row.id, true)
        this.saveMessage = 'Change not saved. Your previous saved value is shown.'
        this.$message.error(error.message)
        if (error.code === -409) this.error = error.message
      } finally {
        this.$set(this.saving, row.id, false)
        if (this.reloadAfterSave && !this.isSaving) this.loadRows()
      }
    },
    async saveLiveSetting(row, column, value) {
      if (row.record_kind !== 'existing' || this.saving[row.id] || this.conflicts[row.id]) return false
      if (JSON.stringify(row[column.key]) === JSON.stringify(value)) return true
      this.$set(this.saving, row.id, true)
      try {
        const data = this.result(await updateAdminOperations({ entity: this.entity, id: row.id, version: row.version, field: column.key, value }))
        this.$set(row, column.key, data[column.key])
        this.saveMessage = `Saved ${column.label} · ${new Date().toLocaleTimeString()}`
        return true
      } catch (error) {
        this.saveMessage = 'Change not saved. Your previous saved value is shown.'
        this.$message.error(error.message)
        return false
      } finally {
        this.$set(this.saving, row.id, false)
      }
    },
    viewPreset(view) {
      if (view === 'Overview') {
        return this.isCurator
          ? ['user_email', 'featured', 'featured_catalogue_section_codes', 'collection_count', 'published_count', 'fields.tracked', 'fields.update_method', 'fields.planning_status', 'fields.batches', 'batch_count']
          : ['user_name', 'status', 'fields.pipeline_status', 'has_cover', 'is_public', 'is_listed', 'fields.assignee', 'fields.email_status']
      }
      if (view === 'All fields') return this.allColumns.map(c => c.key)
      return this.orderedColumns.filter(c => c.group === view || (!c.manual && (this.isCurator ? ['user_email', 'featured', 'featured_catalogue_section_codes', 'batch_count', 'has_signature_intro', 'has_avatar'] : ['user_name', 'has_cover', 'is_public', 'is_listed', 'status']).includes(c.key))).map(c => c.key)
    },
    changeView(view) {
      this.activeView = view
      const saved = this.storedViews[view]
      if (saved && Array.isArray(saved.visibleKeys)) {
        this.visibleKeys = saved.visibleKeys.filter(key => this.allColumns.some(c => c.key === key))
        this.columnOrder = Array.isArray(saved.order) ? saved.order : []
        this.widths = saved.widths && typeof saved.widths === 'object' ? saved.widths : {}
      } else {
        this.visibleKeys = this.viewPreset(view)
        this.columnOrder = []
        this.widths = {}
      }
      this.savePreferences()
    },
    resetView() {
      const storedViews = { ...this.storedViews }
      delete storedViews[this.activeView]
      this.storedViews = storedViews
      this.visibleKeys = this.viewPreset(this.activeView)
      this.columnOrder = []
      this.widths = {}
      this.savePreferences()
    },
    toggleColumn(key) {
      this.visibleKeys = this.visibleKeys.includes(key) ? this.visibleKeys.filter(k => k !== key) : [...this.visibleKeys, key]
      this.savePreferences()
    },
    pickStart(key, event) { this.dragKey = key; event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', key) },
    pickDrop(key, event) {
      const dragged = this.dragKey || event.dataTransfer.getData('text/plain')
      this.dragKey = ''
      if (!dragged || dragged === key) return
      const order = this.orderedColumns.map(c => c.key)
      const from = order.indexOf(dragged)
      if (from < 0) return
      order.splice(from, 1)
      order.splice(order.indexOf(key), 0, dragged)
      this.columnOrder = order
      this.savePreferences()
    },
    saveColumnWidth(newWidth, oldWidth, column) {
      if (!column || !column.property) return
      this.$set(this.widths, column.property, Math.round(newWidth))
      this.savePreferences()
    },
    savePreferences() {
      this.storedViews = { ...this.storedViews, [this.activeView]: { visibleKeys: this.visibleKeys, order: this.columnOrder, widths: this.widths } }
      try { localStorage.setItem(`lf-admin:operations:${this.entity}:views`, JSON.stringify({ schemaVersion: 2, activeView: this.activeView, views: this.storedViews })) } catch (error) { /* Preferences are optional; record data is never stored locally. */ }
      this.$nextTick(() => { if (this.$refs.table) this.$refs.table.doLayout() })
    },
    restorePreferences() {
      try {
        const saved = JSON.parse(localStorage.getItem(`lf-admin:operations:${this.entity}:views`))
        if (saved && this.views.includes(saved.activeView) && saved.views && typeof saved.views === 'object') {
          if (this.isCurator && saved.schemaVersion !== 2 && saved.views.Overview && Array.isArray(saved.views.Overview.visibleKeys)) {
            const key = 'featured_catalogue_section_codes'
            if (!saved.views.Overview.visibleKeys.includes(key)) saved.views.Overview.visibleKeys.push(key)
          }
          this.storedViews = saved.views
          this.changeView(saved.activeView)
          return
        }
      } catch (error) { /* Fall through to the legacy format. */ }
      try {
        const legacy = JSON.parse(localStorage.getItem(`lf-admin:operations:${this.entity}:view`))
        if (legacy && this.views.includes(legacy.activeView) && Array.isArray(legacy.visibleKeys)) {
          this.activeView = legacy.activeView
          this.visibleKeys = legacy.visibleKeys.filter(key => this.allColumns.some(c => c.key === key))
          if (this.isCurator && legacy.activeView === 'Overview' && !this.visibleKeys.includes('featured_catalogue_section_codes')) this.visibleKeys.push('featured_catalogue_section_codes')
          this.savePreferences()
          return
        }
      } catch (error) { /* Fall back to the overview. */ }
      this.changeView('Overview')
    }
  }
}
</script>

<style scoped lang="scss">
.operations-page { padding: 26px; color: #24364b; min-width: 0; }
.page-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 22px; }
.eyebrow { color: #77889b; font-size: 10px; font-weight: 700; letter-spacing: .12em; margin-bottom: 6px; }
h1 { margin: 0; font-size: 26px; letter-spacing: -.025em; }
.heading-actions { display: flex; flex-shrink: 0; }
.plan-actions .el-button { padding: 3px 0; font-size: 11px; }
.delete-action { color: #b0433c !important; }
.page-heading p { margin: 8px 0 0; font-size: 13px; color: #718096; }
.table-panel { background: white; border: 1px solid #e1e7ef; border-radius: 7px; overflow: hidden; }
.view-bar { padding: 14px 18px; display: flex; justify-content: space-between; border-bottom: 1px solid #edf0f4; gap: 16px; }
.view-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
.view-tabs button { border: 0; border-radius: 4px; background: transparent; padding: 8px 14px; color: #6c7a8d; cursor: pointer; font-size: 13px; }
.view-tabs button.active { background: #eaf2fd; color: #165ba8; font-weight: 600; }
.toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; padding: 16px 18px 8px; }
.toolbar > .el-input { width: 260px; }
.toolbar > .el-select { width: 225px; }
.view-count { margin-left: auto; font-size: 12px; color: #64758a; white-space: nowrap; }
.view-count strong { font-weight: 600; color: #24364b; }
.scope-help { margin: 3px 18px 0; font-size: 11px; line-height: 1.5; color: #78889a; }
.internal-note { color: #8598ab; }
.header-hint { color: #9fb0c0; cursor: help; font-size: 13px; }
.save-status { padding: 10px 18px; font-size: 11px; color: #607c71; }
.load-error { margin: 10px 18px; width: auto; }
.record-name { font-weight: 600; color: #263b53; line-height: 1.45; }
.record-id { display: block; color: #8592a2; font-size: 10px; line-height: 1.7; }
.record-links { font-size: 11px; margin-top: 2px; }
.record-links a { color: #2165aa; text-decoration: none; }
.collection-links { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 12px; }
.collection-links a:hover, .collection-links a:focus-visible { text-decoration: underline; }
.auto-label { display: inline-block; font-size: 8px !important; letter-spacing: .04em; border: 1px solid #dbe3eb; color: #8695a6 !important; padding: 0 3px; border-radius: 3px; line-height: 15px; vertical-align: middle; }
.boolean-value { color: #a0abba; font-size: 12px; }
.boolean-value.checked { color: #2b8660; }
.boolean-value i { margin-right: 5px; }
.category-page-select { width: 100%; }
.featured-cell { display: flex; align-items: center; gap: 10px; }
.category-dialog-select { width: 100%; margin-top: 12px; }
.table-footer { display: flex; align-items: center; justify-content: space-between; gap: 15px; padding: 14px 18px; }
.table-footer > span { color: #8a97a6; font-size: 11px; }
.column-picker { max-height: 460px; overflow: auto; }
.column-picker strong { display: block; margin-bottom: 12px; }
.picker-list { max-height: 340px; overflow: auto; margin: 0 -10px; padding: 0 10px; }
.picker-item { cursor: grab; border-radius: 4px; }
.picker-item:hover { background: #f2f6fb; }
.picker-item .el-checkbox { display: flex; align-items: center; margin: 8px 0; }
.picker-handle { color: #a8b6c6; margin-right: 2px; }
.picker-hint { font-size: 10px; color: #93a2b2; margin-top: 6px; }
.reset-view { margin-top: 4px; padding-left: 0; }
::v-deep .el-table { min-height: 280px; font-size: 12px; }
::v-deep .el-table th { background: #f8fafc; color: #647589; font-size: 11px; }
::v-deep .el-table td { padding: 10px 0; }
::v-deep .el-table .el-input__inner { border-color: transparent; background: transparent; padding-left: 6px; font-size: 12px; }
::v-deep .el-table .el-input__inner:hover, ::v-deep .el-table .el-input__inner:focus { border-color: #b8cbe0; }
@media (max-width: 1000px) { .operations-page { padding: 16px; } .table-footer { align-items: flex-start; flex-direction: column; gap: 6px; } }
</style>
